import {useCallback, useMemo, useState} from 'react';
import {observer} from 'mobx-react-lite';
import {useFetchHook} from '@hooks/query-hook';
import {useStoreContext} from '@store/store-context';
import {userName} from '@utils/user-name';
import FeedbackFeed, {normaliseFeedbackResponse} from '../feedback-feed';
import {Feedback} from '../feedback-items';
import {FeedbackDismissModal} from '../feedback-dismiss-modal';
import {FeedbackFilterDivider, FeedbackFilters} from '../feedback-page.styles';
import {
  FeedbackDateControl,
  FeedbackFilterControl,
} from '../feedback-filter-control';
import {usePublicWallHook} from './public-wall.hook';

const idOf = (value: string | {id?: string} | undefined) =>
  typeof value === 'string' ? value : value?.id || '';

const PublicWall = () => {
  const {
    controller,
    dateRange,
    setDateRange,
    filters,
    selectedPerson,
    setSelectedPerson,
    sortBy,
    setSortBy,
  } = usePublicWallHook();
  const {
    usersStore: {users},
  } = useStoreContext();
  const [praiseToDelete, setPraiseToDelete] = useState<Feedback | null>(null);
  const [deletedPraiseIds, setDeletedPraiseIds] = useState<string[]>([]);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchFeedback = useCallback(
    async () => controller.fetchPublicFeedback(filters),
    [controller, filters],
  );

  const feedbackQuery = useFetchHook(
    ['public-wall-feedback', filters],
    fetchFeedback,
  );
  const praise = normaliseFeedbackResponse(feedbackQuery.data).filter(
    ({id}) => !deletedPraiseIds.includes(id),
  );
  const personOptions = useMemo(
    () => [
      {value: '', label: 'Everyone'},
      ...users.map((user) => ({value: user.id, label: userName(user)})),
    ],
    [users],
  );
  const filteredPraise = useMemo(
    () =>
      praise.filter((item) => {
        if (!selectedPerson) return true;
        const recipientIds = item.group?.length
          ? item.group.map(idOf)
          : [idOf(item.user)];
        return recipientIds.includes(selectedPerson);
      }),
    [praise, selectedPerson],
  );

  const deletePraise = useCallback(async () => {
    if (!praiseToDelete) return;

    const praiseId = praiseToDelete.id;
    setIsDeleting(true);
    try {
      const deleted = await controller.patchFeedback(
        {archived: true},
        praiseId,
      );
      if (deleted) {
        setDeletedPraiseIds((ids) =>
          ids.includes(praiseId) ? ids : [...ids, praiseId],
        );
        setPraiseToDelete(null);
        await feedbackQuery.refetch();
      }
    } finally {
      setIsDeleting(false);
    }
  }, [controller, feedbackQuery, praiseToDelete]);

  return (
    <div>
      <FeedbackFilters aria-label="Praise filters">
        <div className="feedback-filter-group">
          <FeedbackFilterControl
            ariaLabel="Filter praise recipient"
            label="Person"
            options={personOptions}
            value={selectedPerson}
            onChange={setSelectedPerson}
            searchable
          />
        </div>
        <div className="feedback-filter-group">
          <FeedbackFilterControl
            ariaLabel="Sort praise"
            label="Sort"
            options={[
              {value: 'desc', label: 'Newest First'},
              {value: 'asc', label: 'Oldest First'},
            ]}
            value={sortBy}
            onChange={setSortBy}
          />
          <FeedbackFilterDivider aria-hidden="true" />
          <FeedbackDateControl
            dateRange={dateRange}
            setDateRange={setDateRange}
          />
        </div>
      </FeedbackFilters>
      <FeedbackFeed
        variant="praise"
        data={filteredPraise}
        isLoading={feedbackQuery.isLoading}
        sortOrder={sortBy as 'asc' | 'desc'}
        pageKey={JSON.stringify([filters, selectedPerson, sortBy])}
        emptyState={
          dateRange.preset !== 'all' || selectedPerson
            ? {
                title: 'No praise found',
                description: 'Try changing the person or date filters.',
              }
            : undefined
        }
        onDeletePraise={setPraiseToDelete}
      />
      <FeedbackDismissModal
        action="delete-praise"
        request={praiseToDelete}
        isLoading={isDeleting}
        onClose={() => setPraiseToDelete(null)}
        onConfirm={deletePraise}
      />
    </div>
  );
};

export default observer(PublicWall);
